/**
 * Настройки → Уведомления: где живёт бот и когда он присылает сводку
 * (docs/05-integrations.md).
 *
 * Бота можно добавить в групповой чат. Пока в приложении ничего не включено,
 * он там молчит и на команды не отвечает: добавить бота может кто угодно,
 * а решать, что ему можно, должна команда.
 */
import { useState, type CSSProperties } from 'react';
import { Badge, Button, Card, Checkbox, Icon, Select } from '@/ds';
import { Provenance } from '@/components/money';
import {
  useNotificationSettings,
  useOperation,
  type DigestFrequency,
  type TelegramPlace,
} from '@/api/hooks';
import { formatDateTime } from '@/lib/format';
import { useControlSize } from '@/lib/useControlSize';

const SECTION_TITLE: CSSProperties = {
  font: '500 16px var(--font-display)',
  letterSpacing: 'var(--ls-heading)',
  color: 'var(--text-strong)',
};

const WEEKDAYS = [
  { value: '1', label: 'понедельник' },
  { value: '2', label: 'вторник' },
  { value: '3', label: 'среда' },
  { value: '4', label: 'четверг' },
  { value: '5', label: 'пятница' },
  { value: '6', label: 'суббота' },
  { value: '7', label: 'воскресенье' },
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Раз в неделю' },
  { value: 'daily', label: 'Каждый день' },
  { value: 'biweekly', label: 'Раз в две недели' },
  { value: 'off', label: 'Не отправлять' },
];

/**
 * Регулярная сводка по буксующим задачам. Тот же отчёт отдаёт команда
 * бота, здесь задаётся только частота.
 */
export function TaskDigestCard() {
  const size = useControlSize();
  const settings = useNotificationSettings(true);
  const save = useOperation<
    { frequency?: DigestFrequency; weekday?: number; hour?: number },
    { frequency: string }
  >('tasks.set_digest');
  const sendNow = useOperation<Record<string, never>, { sent: number; tasks: number }>(
    'tasks.send_digest',
  );
  const [sent, setSent] = useState<string | null>(null);

  const digest = settings.data?.taskDigest;
  if (!digest) return null;

  const off = digest.frequency === 'off';

  return (
    <Card padding="md">
      <div style={{ ...SECTION_TITLE, marginBottom: 'var(--space-4)' }}>Сводка по задачам</div>

      <p style={{ font: '400 14px/1.6 var(--font-text)', color: 'var(--text-muted)', maxWidth: 560 }}>
        Бот сам присылает задачи, которые выбились из срока: те, у кого срок начался, а статус
        всё ещё Not started, и те, у кого срок прошёл, а работа не закончена. Сводка уходит
        в личные чаты и в группы, где уведомления разрешены. Выбиваться нечему — сообщения не будет.
      </p>

      <div
        style={{
          marginTop: 'var(--space-4)',
          display: 'flex',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: '0 1 200px' }}>
          <Select
            label="Частота"
            size={size}
            options={FREQUENCIES}
            value={digest.frequency}
            onChange={(e) => save.mutate({ frequency: e.target.value as DigestFrequency })}
          />
        </div>

        {digest.frequency === 'weekly' || digest.frequency === 'biweekly' ? (
          <div style={{ flex: '0 1 180px' }}>
            <Select
              label="День"
              size={size}
              options={WEEKDAYS}
              value={String(digest.weekday)}
              onChange={(e) => save.mutate({ weekday: Number(e.target.value) })}
            />
          </div>
        ) : null}

        {off ? null : (
          <div style={{ flex: '0 1 140px' }}>
            <Select
              label="Время"
              size={size}
              options={Array.from({ length: 24 }, (_, hour) => ({
                value: String(hour),
                label: `${String(hour).padStart(2, '0')}:00`,
              }))}
              value={String(digest.hour)}
              onChange={(e) => save.mutate({ hour: Number(e.target.value) })}
            />
          </div>
        )}

        <Button
          variant="ghost"
          size={size}
          loading={sendNow.isPending}
          onClick={() =>
            sendNow.mutate({} as Record<string, never>, {
              onSuccess: (result) =>
                setSent(
                  result.tasks === 0
                    ? 'Отправлять нечего: все задачи идут по сроку'
                    : `Отправлено адресатам: ${result.sent}`,
                ),
            })
          }
        >
          Отправить сейчас
        </Button>
      </div>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <Provenance>
          {off
            ? 'Сводка выключена. Команда /stuck в боте продолжает работать.'
            : `Время московское. Последняя отправка: ${digest.lastSentOn ?? 'ещё не было'}.`}
        </Provenance>
        {sent ? <Provenance>{sent}</Provenance> : null}
      </div>
    </Card>
  );
}

/** Одна строка настроек: группа целиком или отдельный топик. */
function PlaceRow({
  title,
  hint,
  notifications,
  commands,
  disabled,
  onChange,
}: {
  title: string;
  hint: string;
  notifications: boolean;
  commands: boolean;
  disabled: boolean;
  onChange: (patch: { notificationsEnabled?: boolean; commandsEnabled?: boolean }) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-4)',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingBottom: 'var(--space-4)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ font: '500 14px var(--font-text)', color: 'var(--text-strong)' }}>{title}</div>
        <Provenance>{hint}</Provenance>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-5)', alignItems: 'center', flexWrap: 'wrap' }}>
        <Checkbox
          label="Уведомления"
          checked={notifications}
          disabled={disabled}
          onChange={(e) => onChange({ notificationsEnabled: e.target.checked })}
        />
        <Checkbox
          label="Команды"
          checked={commands}
          disabled={disabled}
          onChange={(e) => onChange({ commandsEnabled: e.target.checked })}
        />
      </div>
    </div>
  );
}

function PlaceCard({ place }: { place: TelegramPlace }) {
  const set = useOperation<
    {
      chatId: string;
      topicId?: string | null;
      notificationsEnabled?: boolean;
      commandsEnabled?: boolean;
    },
    { chatId: string }
  >('telegram.set_place');
  const forget = useOperation<{ chatId: string }, { chatId: string }>('telegram.forget_place');

  const locked = set.isPending || Boolean(place.removedAt);

  return (
    <div style={{ paddingBottom: 'var(--space-5)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-4)',
        }}
      >
        <span style={{ font: '500 15px var(--font-text)', color: 'var(--text-strong)' }}>
          {place.title}
        </span>
        <Badge tone="neutral">{place.isForum ? 'с топиками' : 'группа'}</Badge>
        {place.removedAt ? <Badge tone="warning">бота нет в группе</Badge> : null}
        <span style={{ marginLeft: 'auto' }}>
          <Button
            variant="ghost"
            size="sm"
            loading={forget.isPending}
            onClick={() => forget.mutate({ chatId: place.id })}
          >
            Забыть
          </Button>
        </span>
      </div>

      {place.isForum ? (
        place.topics.length === 0 ? (
          <Provenance>
            Топики ещё не встречались. Telegram не даёт их перечислить: напишите в нужном топике
            любое сообщение, и он появится здесь.
          </Provenance>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {place.topics.map((topic) => (
              <PlaceRow
                key={topic.id}
                title={topic.name}
                hint={`Топик ${topic.threadId}`}
                notifications={topic.notificationsEnabled}
                commands={topic.commandsEnabled}
                disabled={locked}
                onChange={(patch) => set.mutate({ chatId: place.id, topicId: topic.id, ...patch })}
              />
            ))}
          </div>
        )
      ) : (
        <PlaceRow
          title="Вся группа"
          hint="Топиков нет, настройка одна на весь чат"
          notifications={place.notificationsEnabled}
          commands={place.commandsEnabled}
          disabled={locked}
          onChange={(patch) => set.mutate({ chatId: place.id, topicId: null, ...patch })}
        />
      )}

      <div style={{ marginTop: 'var(--space-3)' }}>
        <Provenance>{`Бот добавлен ${formatDateTime(place.addedAt)}`}</Provenance>
      </div>
    </div>
  );
}

/** Где живёт бот: список групп и топиков со своими разрешениями. */
export function PlacesCard() {
  const settings = useNotificationSettings(true);
  const places = settings.data?.places ?? [];
  const telegram = settings.data?.telegram;

  const invite = telegram?.botUsername
    ? `https://t.me/${telegram.botUsername}?startgroup=true`
    : null;

  return (
    <Card padding="md">
      <div style={{ ...SECTION_TITLE, marginBottom: 'var(--space-4)' }}>Группы и топики</div>

      <p style={{ font: '400 14px/1.6 var(--font-text)', color: 'var(--text-muted)', maxWidth: 560 }}>
        Пока здесь ничего не включено, бот в группе молчит и на команды не отвечает. Уведомления
        и работа с ботом включаются отдельно: в один топик можно только писать, в другом — только
        спрашивать. В группе с топиками настройки задаются для каждого топика.
      </p>

      {places.length === 0 ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Provenance>
            Бот пока ни в одной группе. Добавьте его обычным участником — группа появится здесь сама.
          </Provenance>
        </div>
      ) : (
        <div
          style={{
            marginTop: 'var(--space-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-5)',
          }}
        >
          {places.map((place) => (
            <PlaceCard key={place.id} place={place} />
          ))}
        </div>
      )}

      {invite ? (
        <div style={{ marginTop: 'var(--space-5)' }}>
          <Button
            variant="secondary"
            size="sm"
            as="a"
            href={invite}
            iconRight={<Icon name="ExternalLink" size={15} />}
          >
            Добавить в группу
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
